import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { redirectToPayment } from '../../services/stripeService';
import './Subscriptions.css';

const Subscriptions = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const plansSectionRef = useRef(null);
  const descriptionSectionRef = useRef(null);
  const priceDisclaimer = t('subscriptions.priceDisclaimer', {
    defaultValue: (i18n.language || 'en').startsWith('es')
      ? 'Los precios se expresan en USD (dólares estadounidenses).'
      : 'Prices are expressed in USD (U.S. dollars).'
  });

  // Language toggle handler
  const handleLanguageToggle = (lang) => {
    i18n.changeLanguage(lang);
  };

  // Animate page hero on load
  useEffect(() => {
    const heroTitle = document.querySelector('.page-hero h1');
    const heroSubheadline = document.querySelector('.page-hero .subheadline');
    
    setTimeout(() => {
      if (heroTitle) {
        heroTitle.style.animation = 'planCardFadeIn 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards';
      }
      if (heroSubheadline) {
        heroSubheadline.style.animation = 'planCardFadeIn 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.2s forwards';
      }
    }, 100);
  }, []);

  // Scroll-triggered animations for subscription plans section
  useEffect(() => {
    const plansSection = plansSectionRef.current;
    if (!plansSection) return;

    const observerOptions = {
      threshold: 0.2,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          plansSection.classList.add('visible');
          
          // Animate checkmark paths with staggered delays
          const checkmarkPaths = plansSection.querySelectorAll('.checkmark-path');
          checkmarkPaths.forEach((path, index) => {
            setTimeout(() => {
              path.style.animation = 'checkmarkDraw 0.6s ease-out forwards';
            }, 500 + (index * 100));
          });
          
          observer.unobserve(plansSection);
        }
      });
    }, observerOptions);

    observer.observe(plansSection);

    return () => {
      if (plansSection) {
        observer.unobserve(plansSection);
      }
    };
  }, []);

  // Scroll-triggered animations for product description section
  useEffect(() => {
    const descriptionSection = descriptionSectionRef.current;
    if (!descriptionSection) return;

    const observerOptions = {
      threshold: 0.2,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          descriptionSection.classList.add('visible');
          observer.unobserve(descriptionSection);
        }
      });
    }, observerOptions);

    observer.observe(descriptionSection);

    return () => {
      if (descriptionSection) {
        observer.unobserve(descriptionSection);
      }
    };
  }, []);

  // Handle subscribe button click - redirect to Stripe Payment Link
  const handleSubscribe = (planType) => {
    console.log('[Subscriptions] Subscribe button clicked for plan:', planType);
    
    // Check if user is authenticated
    if (!isAuthenticated || !user || !user.uid) {
      // Redirect to signup first
      navigate('/signup');
      return;
    }
    
    // Use the centralized service function with return URL
    const returnUrl = `${window.location.origin}/account`;
    const result = redirectToPayment(planType, user.uid, returnUrl);
    
    if (!result.success) {
      console.error('[Subscriptions] Failed to redirect to payment:', result.error);
      navigate('/account');
    }
  };

  return (
    <div className="subscriptions-wrapper">
      {/* Page Header */}
      <header className="page-header">
        <div className="header-logo">
          <button
            className="language-toggle"
            aria-label={t('subscriptions.topBar.toggleLanguage')}
            onClick={(e) => {
              const option = e.target.closest('.lang-option');
              if (option) {
                const targetLang = option.dataset.lang;
                if (targetLang) {
                  handleLanguageToggle(targetLang);
                }
              }
            }}
          >
            <span 
              className={`lang-option ${i18n.language === 'en' ? 'active' : ''}`} 
              data-lang="en"
            >
              EN
            </span>
            <span className="lang-separator">|</span>
            <span 
              className={`lang-option ${i18n.language === 'es' ? 'active' : ''}`} 
              data-lang="es"
            >
              ES
            </span>
          </button>
          <Link to="/landing">
            <svg
              id="Component_13_5"
              data-name="Component 13 – 5"
              xmlns="http://www.w3.org/2000/svg"
              xmlnsXlink="http://www.w3.org/1999/xlink"
              width="86.609"
              height="77.109"
              viewBox="0 0 86.609 77.109"
            >
              <defs>
                <clipPath id={`clip-path-subscriptions-${i18n.language}`}>
                  <rect
                    id="Rectangle_351"
                    data-name="Rectangle 351"
                    width="42.305"
                    height="48.895"
                    transform="translate(-3 -6)"
                    fill="none"
                    stroke="#49a9ff"
                    strokeLinejoin="round"
                    strokeWidth="2"
                  />
                </clipPath>
              </defs>
              <path
                id="Path_38578"
                data-name="Path 38578"
                d="M31.061,4.859,14.374,37.091.435,65.389c-1.175,2.385.114,4.365,2.866,4.4l27.76.34,27.76-.34c2.752-.034,4.041-2.013,2.866-4.4l-13.94-28.3Z"
                transform="translate(0 6.982)"
                fill="#49a9ff"
              />
              <g id="Group_11876" data-name="Group 11876" transform="translate(48.422 6)" style={{ isolation: 'isolate' }}>
                <g id="Group_11849" data-name="Group 11849" transform="translate(-1.119 0)">
                  <g id="Group_11848" data-name="Group 11848" clipPath={`url(#clip-path-subscriptions-${i18n.language})`}>
                    <path
                      id="Path_38576"
                      data-name="Path 38576"
                      d="M45.006,46.045,54.8,27.824l8.179-15.995a1.754,1.754,0,0,0-1.683-2.488L45.006,9.15l-16.289.191a1.754,1.754,0,0,0-1.681,2.488l8.179,15.995Z"
                      transform="translate(-26.854 -9.149)"
                      fill="none"
                      stroke="#49a9ff"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    />
                  </g>
                </g>
              </g>
            </svg>
          </Link>
        </div>
        <Link to="/landing" className="back-home-button">
          {t('subscriptions.topBar.backHome')}
        </Link>
      </header>

      {/* Page Hero */}
      <section className="page-hero">
        <h1>{t('subscriptions.hero.title')}</h1>
        <p className="subheadline">{t('subscriptions.hero.subheadline')}</p>
        <div className="scroll-indicator">
          <p className="scroll-indicator-text">{t('subscriptions.hero.scrollIndicator')}</p>
          <div className="scroll-arrow">
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 13l5 5 5-5M7 6l5 5 5-5" />
            </svg>
          </div>
        </div>
        <p className="price-disclaimer">{priceDisclaimer}</p>
      </section>

      {/* Subscription Plans Section */}
      <section 
        ref={plansSectionRef}
        className="subscription-plans-section"
      >
        <div className="subscription-plans-container">
          <div className="plans-grid">
            {t('subscriptions.plans', { returnObjects: true }).map((plan, index) => (
              <div 
                key={index}
                className={`plan-card ${plan.featured ? 'featured' : ''}`}
              >
                <div className="plan-card-content">
                  {plan.tag && (
                    <span className={`plan-tag ${plan.tag === 'Premium' ? 'plan-tag-premium' : ''}`}>
                      {plan.tag}
                    </span>
                  )}
                  <h2 
                    className="plan-title"
                    dangerouslySetInnerHTML={{ __html: plan.title }}
                  />
                  <div className="plan-price">
                    {plan.price}
                    {plan.pricePeriod && (
                      <span className="plan-price-period">{plan.pricePeriod}</span>
                    )}
                  </div>
                  {plan.description && (
                    <p className="plan-description">{plan.description}</p>
                  )}
                  <ul className="plan-features">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="plan-feature-item">
                        <svg 
                          className="checkmark-icon" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="3" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <path 
                            className="checkmark-path" 
                            d="M20 6L9 17l-5-5"
                          />
                        </svg>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button 
                    className="plan-button"
                    onClick={() => handleSubscribe(plan.type)}
                  >
                    {plan.buttonText}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="scroll-indicator">
            <p className="scroll-indicator-text">{t('subscriptions.hero.scrollIndicator')}</p>
            <div className="scroll-arrow">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 13l5 5 5-5M7 6l5 5 5-5" />
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Product Description Section */}
      <section 
        ref={descriptionSectionRef}
        className="product-description-section"
      >
        <div className="product-description-container">
          <h2 className="section-headline">{t('subscriptions.productDescription.headline')}</h2>
          <p className="section-subheadline">{t('subscriptions.productDescription.subheadline')}</p>

          <div className="description-content">
            {t('subscriptions.productDescription.sections', { returnObjects: true }).map((section, index) => (
              <div key={index} className="description-block">
                <h3>{section.title}</h3>
                {Array.isArray(section.content) ? (
                  <>
                    {section.content.map((paragraph, pIndex) => (
                      <p key={pIndex}>{paragraph}</p>
                    ))}
                  </>
                ) : (
                  <p>{section.content}</p>
                )}
                {section.list && (
                  <ul>
                    {section.list.map((item, itemIndex) => (
                      <li key={itemIndex}>{item}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          {/* Dashboard Cards */}
          <div className="dashboard-options">
            {t('subscriptions.dashboards', { returnObjects: true }).map((dashboard, index) => (
              <div key={index} className="dashboard-card">
                <h4>{dashboard.title}</h4>
                <p>{dashboard.description}</p>
              </div>
            ))}
          </div>

          <div className="description-content" style={{ marginTop: '48px' }}>
            <div className="description-block">
              <p>
                <strong>{t('subscriptions.productDescription.importantNote.prefix')}</strong>{' '}
                {t('subscriptions.productDescription.importantNote.text')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="page-footer">
        <p>
          {t('subscriptions.footer.ready')}{' '}
          <Link to="/landing">{t('subscriptions.footer.returnLink')}</Link>{' '}
          {t('subscriptions.footer.orChoose')}
        </p>
        <p style={{ marginTop: '16px', fontSize: '0.85rem', opacity: 0.8 }}>
          {t('subscriptions.footer.disclaimer')}
        </p>
      </footer>
    </div>
  );
};

export default Subscriptions;
